import {dispatch} from 'od-modules/secure_store/store';
import {
    SET_SELECTED_PRODUCTS,
    SET_RESIDENT_DISCOUNT,
    SET_SELECTED_PAYMENT_METHOD,
} from 'od-modules/secure_store/actions';
const Dictionary = require('./cart.dictionary');
const ExtraInfo = require('./extra.info');

const STORAGE_KEY = 'ancillariesShoppingCart';
const VERSION = '0.2';

const getDataFromStorage = () => Odigeo.Utils.parseJson(Odigeo.Session.get(STORAGE_KEY) || '{}');

const loadFromStorage = (data = {}, currentVersion) => data.version === currentVersion ? data : {};

const loadData = (version) => loadFromStorage(getDataFromStorage(), version);

const getSession = () => Odigeo.Session || {set: () => {}};

const persistInSession = (data = {}) => getSession().set(STORAGE_KEY, JSON.stringify(data));

const getIdList = (items = []) => items.map(({id}) => id);

const getFirstElement = ([first = {}, _] = []) => first;

const updateItem = (item, {success = false, price = 0} = {}) => item ? item.update({success, price}) : false;

const committableFilter = ({committable = false}) => committable;

class CartCollection {
    constructor() {
        this.restored = false;
        this.init();
    }

    init(bookingId = null) {
        this.load({bookingId});

        if (bookingId) {
            this.persist();
        }
    }

    load(data = {}) {
        this.addedItems = new Dictionary(data.productsToAdd);
        this.removedItems = new Dictionary(data.productsToRemove);
        this.recycleBin = new Dictionary(data.recycleBin);

        ExtraInfo.init(data);
    }

    clear() {
        this.init();
        dispatch({type: SET_SELECTED_PRODUCTS, entry: []});
        dispatch({type: SET_SELECTED_PAYMENT_METHOD, entry: {}});
        dispatch({type: SET_RESIDENT_DISCOUNT, entry: false});
        persistInSession({});
    }

    addProduct(data = {}) {
        this.addedItems.addItem(this.removedItems.removeItem(data.name) || data);

        return this.persist();
    }

    updateProduct(data = {}) {
        this.addedItems.addItem(data);

        return this.persist();
    }

    removeProduct(name) {
        if (this.containsProduct(name)) {
            this.deleteProduct(name);
        }

        return this.persist();
    }

    deleteProduct(name) {
        this.removedItems.addItem(this.addedItems.removeItem(name));
    }

    containsProduct(name) {
        return this.addedItems.hasItem(name);
    }

    hasProductBeenRemoved(name) {
        return this.removedItems.hasItem(name);
    }

    getProduct(name) {
        return this.addedItems.getItem(name);
    }

    persist() {
        persistInSession(this.getPlainItems());
        dispatch({type: SET_SELECTED_PRODUCTS, entry: JSON.stringify(this.addedItems.getItems())});

        return this;
    }

    restore() {
        const data = loadData(this.version);
        const productsToAdd = data.productsToAdd || [];
        const dataToBeRestored = productsToAdd.filter((item) => item.toBeRestored);
        this.load({
            ...data,
            productsToAdd: dataToBeRestored,
        });
        this.restored = true;
    }

    mustBeCommitted() {
        return !this.isCommitted();
    }

    isCommitted() {
        return this.addedItems.isEveryItemCommitted() && this.removedItems.isEveryItemNotCommitted();
    }

    getAllItems(filter = () => true) {
        return {
            productsToAdd: this.addedItems.getItems(filter),
            productsToRemove: this.removedItems.getItems(filter),
        };
    }

    getUncommittedItems(filter = () => true) {
        return {
            productsToAdd: this.addedItems.getNotCommittedItems(filter),
            productsToRemove: this.removedItems.getCommittedItems(filter),
        };
    }

    getUncommittedIds(filter = () => true) {
        return {
            productsToAdd: getIdList(this.addedItems.getNotCommittedItems(filter)),
            productsToRemove: getIdList(this.removedItems.getCommittedItems(filter)),
        };
    }

    isAnythingToBeCommitted() {
        return this.addedItems.getNotCommittedItems(committableFilter).length > 0
            || this.removedItems.getCommittedItems(committableFilter).length > 0;
    }

    getPlainItems() {
        return {
            ...ExtraInfo.data,
            productsToAdd: this.addedItems.getItems(),
            productsToRemove: this.removedItems.getItems(),
            recycleBin: this.recycleBin.getItems(),
            version: this.version,
        };
    }

    setBookingId(id) {
        if (!this.restored) {
            this.restore();
        }

        if (!ExtraInfo.getBookingId()) {
            this.init(id);
        } else if (Odigeo.AB.isUpdateShoppingCartAncillaries && ExtraInfo.getBookingId() !== id) {
            this.updateBookingId(id);
            this.persist();
        }
    }

    updateBookingId(id) {
        ExtraInfo.setBookingId(id);
    }

    reprocessCommittedElements() {
        this.addedItems.getRawItems(({committed}) => committed)
            .forEach((item) => item.forceCommit());
        this.persist();
    }

    setBuyerInfo(info = {}) {
        ExtraInfo.setBuyerInfo(info);
        this.persist();
    }

    findItemById(idToFind) {
        return [
            ...this.addedItems.getRawItems(({id}) => id === idToFind),
            ...this.removedItems.getRawItems(({id}) => id === idToFind),
        ];
    }

    findFirstItemById({id}) {
        return getFirstElement(this.findItemById(id));
    }

    get version() {
        return VERSION;
    }

    updateItems(items = []) {
        items.map((item) => {
            updateItem(this.findFirstItemById(item), item);
        });

        const removed = this.removedItems.getRawItems(({committed}) => committed);

        this.removedItems.removeItems(removed);
        this.recycleBin.addItems(removed);

        this.persist();
    }

    get commitData() {
        const products = this.addedItems.setToBeRestored();
        persistInSession({
            ...this.getPlainItems(),
            productsToAdd: products,
        });
        dispatch({type: SET_SELECTED_PRODUCTS, entry: JSON.stringify(products)});
        return {
            bookingId: ExtraInfo.getBookingId(),
            phone: ExtraInfo.phone,
            ...this.getUncommittedIds(committableFilter),
        };
    }
}

export default CartCollection;
